import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Shop items configuration
export const CONSUMABLES = [
  {
    id: 'health_potion',
    name: 'Health Potion',
    description: 'Restore 40 HP instantly',
    emoji: '🧪',
    cost: 2,
    effect: { type: 'heal', value: 40 }
  },
  {
    id: 'super_health_potion',
    name: 'Super Health Potion',
    description: 'Restore 80 HP instantly',
    emoji: '💊',
    cost: 4,
    effect: { type: 'heal', value: 80 }
  },
  {
    id: 'striped_fruit',
    name: 'Striped Fruit',
    description: 'Place a striped fruit on the board',
    emoji: '🍬',
    cost: 3,
    effect: { type: 'striped' }
  },
  {
    id: 'extra_moves',
    name: 'Extra Moves',
    description: '+3 moves during level',
    emoji: '🔄',
    cost: 2,
    effect: { type: 'moves', value: 3 }
  },
  {
    id: 'double_xp',
    name: '2X XP',
    description: 'Double XP for 20 turns',
    emoji: '✨',
    cost: 5,
    effect: { type: 'doubleXP', duration: 20 }
  },
  {
    id: 'bomb',
    name: 'Bomb',
    description: 'Destroy 3x3 area',
    emoji: '💣',
    cost: 3,
    effect: { type: 'bomb', size: 3 }
  },
  {
    id: 'color_bomb',
    name: 'Color Bomb',
    description: 'Remove all fruits of 1 type',
    emoji: '💣',
    cost: 4,
    effect: { type: 'colorBomb' }
  },
  {
    id: 'shuffle',
    name: 'Shuffle',
    description: 'Reshuffle the entire board',
    emoji: '🔀',
    cost: 2,
    effect: { type: 'shuffle' }
  },
  {
    id: 'wrapped_fruit',
    name: 'Wrapped Fruit',
    description: 'Place a wrapped fruit on the board',
    emoji: '🎁',
    cost: 3,
    effect: { type: 'wrapped' }
  },
  {
    id: 'disco_fruit',
    name: 'Disco Fruit',
    description: 'Place a disco melon on the board',
    emoji: '🪩',
    cost: 3,
    effect: { type: 'disco' }
  },
  // === MILESTONE REWARD ITEMS (Random drops from levels) ===
  {
    id: 'crystal_ball',
    name: 'Crystal Ball',
    description: 'Preview next 3 rows of falling fruits',
    emoji: '🔮',
    cost: 0, // Not purchasable, only from drops
    effect: { type: 'preview', rows: 3 },
    droppable: true,
    dropLevel: 25,
    dropChance: 0.15, // 15% chance
    rarity: 'rare'
  },
  {
    id: 'fruit_magnet',
    name: 'Fruit Magnet',
    description: 'Pull all matching fruits together',
    emoji: '🧲',
    cost: 0,
    effect: { type: 'magnet' },
    droppable: true,
    dropLevel: 75,
    dropChance: 0.12, // 12% chance
    rarity: 'epic'
  },
  {
    id: 'dragon_scale',
    name: 'Dragon Scale',
    description: '+50% damage to bosses',
    emoji: '🐉',
    cost: 0,
    effect: { type: 'bossDamage', value: 1.5 },
    droppable: true,
    dropLevel: 100,
    dropChance: 0.10, // 10% chance
    rarity: 'epic'
  },
  {
    id: 'moon_stone',
    name: 'Moon Stone',
    description: 'Highlights best moves for 30 seconds',
    emoji: '🌙',
    cost: 0,
    effect: { type: 'highlight', duration: 30 },
    droppable: true,
    dropLevel: 125,
    dropChance: 0.10,
    rarity: 'rare'
  },
  {
    id: 'thunder_strike',
    name: 'Thunder Strike',
    description: 'Clear random column + row instantly',
    emoji: '⚡',
    cost: 0,
    effect: { type: 'thunderStrike' },
    droppable: true,
    dropLevel: 150,
    dropChance: 0.08, // 8% chance
    rarity: 'legendary'
  },
  // === ACHIEVEMENT REWARD ITEMS ===
  {
    id: 'lucky_clover',
    name: 'Lucky Clover',
    description: '+15% special fruit spawn rate for level',
    emoji: '🍀',
    cost: 0,
    effect: { type: 'luckySpawn', value: 0.15 },
    droppable: true,
    achievement: 'star_collector', // 3-star 10 levels
    dropChance: 0.12,
    rarity: 'rare'
  },
  {
    id: 'guardian_shield',
    name: 'Guardian Shield',
    description: 'Block 1 boss attack per fight',
    emoji: '🛡️',
    cost: 0,
    effect: { type: 'blockBoss', blocks: 1 },
    droppable: true,
    achievement: 'boss_slayer', // Win 5 boss fights
    dropChance: 0.10,
    rarity: 'epic'
  },
  {
    id: 'tsunami_wave',
    name: 'Tsunami Wave',
    description: 'Clear bottom 2 rows instantly',
    emoji: '🌊',
    cost: 0,
    effect: { type: 'clearRows', rows: 2 },
    droppable: true,
    achievement: 'cascade_master', // 50 cascade combos
    dropChance: 0.12,
    rarity: 'epic'
  },
  {
    id: 'rainbow_aura',
    name: 'Rainbow Aura',
    description: 'Start level with 1 random special fruit',
    emoji: '💣',
    cost: 0,
    effect: { type: 'startSpecial' },
    droppable: true,
    achievement: 'fruit_collector', // Collect 10,000 fruits
    dropChance: 0.15,
    rarity: 'rare'
  },
  // === OCEAN DEPTHS WORLD DROPS (141-160) ===
  {
    id: 'ocean_pearl',
    name: 'Ocean Pearl',
    description: 'Creates bubble shield blocking 1 obstacle spawn',
    emoji: '🦪',
    cost: 0,
    effect: { type: 'bubbleShield' },
    droppable: true,
    dropLevel: 141,
    maxLevel: 160,
    dropChance: 0.18,
    rarity: 'rare',
    world: 'ocean_depths'
  },
  {
    id: 'trident_shard',
    name: 'Trident Shard',
    description: 'Clear 3 random rows with water blast',
    emoji: '🔱',
    cost: 0,
    effect: { type: 'clearRows', rows: 3 },
    droppable: true,
    dropLevel: 145,
    maxLevel: 160,
    dropChance: 0.12,
    rarity: 'epic',
    world: 'ocean_depths'
  },
  {
    id: 'kraken_tentacle',
    name: 'Kraken Tentacle',
    description: 'Grab and remove 8 random obstacles',
    emoji: '🦑',
    cost: 0,
    effect: { type: 'removeObstacles', count: 8 },
    droppable: true,
    dropLevel: 155,
    maxLevel: 160,
    dropChance: 0.06,
    rarity: 'legendary',
    world: 'ocean_depths'
  },
  {
    id: 'coral_charm',
    name: 'Coral Charm',
    description: '+20% score for water-themed levels',
    emoji: '🪸',
    cost: 0,
    effect: { type: 'scoreBonus', value: 0.2 },
    droppable: true,
    dropLevel: 141,
    maxLevel: 160,
    dropChance: 0.20,
    rarity: 'rare',
    world: 'ocean_depths'
  },
  {
    id: 'deep_sea_compass',
    name: 'Deep Sea Compass',
    description: 'Shows hidden matches for 20 seconds',
    emoji: '🧭',
    cost: 0,
    effect: { type: 'showMatches', duration: 20 },
    droppable: true,
    dropLevel: 150,
    maxLevel: 160,
    dropChance: 0.10,
    rarity: 'epic',
    world: 'ocean_depths'
  },
  // === FROZEN PEAKS WORLD DROPS (161-180) ===
  {
    id: 'frost_crystal',
    name: 'Frost Crystal',
    description: 'Freeze and shatter 5 random tiles',
    emoji: '❄️',
    cost: 0,
    effect: { type: 'frostShatter', count: 5 },
    droppable: true,
    dropLevel: 161,
    maxLevel: 180,
    dropChance: 0.18,
    rarity: 'rare',
    world: 'frozen_peaks'
  },
  {
    id: 'aurora_essence',
    name: 'Aurora Essence',
    description: 'Creates rainbow cascade clearing matching colors',
    emoji: '🌌',
    cost: 0,
    effect: { type: 'auroraEffect' },
    droppable: true,
    dropLevel: 165,
    maxLevel: 180,
    dropChance: 0.12,
    rarity: 'epic',
    world: 'frozen_peaks'
  },
  {
    id: 'yeti_fur',
    name: 'Yeti Fur',
    description: 'Immune to freeze effects for entire level',
    emoji: '🦣',
    cost: 0,
    effect: { type: 'freezeImmune' },
    droppable: true,
    dropLevel: 175,
    maxLevel: 180,
    dropChance: 0.06,
    rarity: 'legendary',
    world: 'frozen_peaks'
  },
  {
    id: 'ice_pick',
    name: 'Ice Pick',
    description: 'Break through 3 layers of ice instantly',
    emoji: '⛏️',
    cost: 0,
    effect: { type: 'breakFrozen', layers: 3 },
    droppable: true,
    dropLevel: 161,
    maxLevel: 180,
    dropChance: 0.20,
    rarity: 'rare',
    world: 'frozen_peaks'
  },
  {
    id: 'snow_globe',
    name: 'Snow Globe',
    description: 'Freeze all obstacles for 3 turns',
    emoji: '🔮',
    cost: 0,
    effect: { type: 'freezeObstacles', turns: 3 },
    droppable: true,
    dropLevel: 170,
    maxLevel: 180,
    dropChance: 0.10,
    rarity: 'epic',
    world: 'frozen_peaks'
  },
  // === ANCIENT RUINS WORLD DROPS (181-200) ===
  {
    id: 'pharaoh_scarab',
    name: "Pharaoh's Scarab",
    description: 'Destroy all curse tiles on the board',
    emoji: '🪲',
    cost: 0,
    effect: { type: 'removeCurse' },
    droppable: true,
    dropLevel: 181,
    maxLevel: 200,
    dropChance: 0.18,
    rarity: 'rare',
    world: 'ancient_ruins'
  },
  {
    id: 'ankh_of_life',
    name: 'Ankh of Life',
    description: 'Get second chance if level fails',
    emoji: '☥',
    cost: 0,
    effect: { type: 'extraLife' },
    droppable: true,
    dropLevel: 185,
    maxLevel: 200,
    dropChance: 0.10,
    rarity: 'epic',
    world: 'ancient_ruins'
  },
  {
    id: 'eye_of_ra',
    name: 'Eye of Ra',
    description: 'Reveals all hidden matches for 3 seconds',
    emoji: '👁️',
    cost: 0,
    effect: { type: 'revealMatches', duration: 3 },
    droppable: true,
    dropLevel: 195,
    maxLevel: 200,
    dropChance: 0.05,
    rarity: 'legendary',
    world: 'ancient_ruins'
  },
  {
    id: 'golden_key',
    name: 'Golden Key',
    description: 'Unlock all locked tiles on the board',
    emoji: '🗝️',
    cost: 0,
    effect: { type: 'unlockAll' },
    droppable: true,
    dropLevel: 181,
    maxLevel: 200,
    dropChance: 0.20,
    rarity: 'rare',
    world: 'ancient_ruins'
  },
  {
    id: 'sand_timer',
    name: 'Sand Timer',
    description: 'Freeze all sand traps for 10 moves',
    emoji: '⏳',
    cost: 0,
    effect: { type: 'freezeSandTraps', moves: 10 },
    droppable: true,
    dropLevel: 190,
    maxLevel: 200,
    dropChance: 0.12,
    rarity: 'epic',
    world: 'ancient_ruins'
  },
  {
    id: 'mummy_wrap',
    name: 'Mummy Wrap',
    description: 'Wrap and contain spreading obstacles',
    emoji: '🧻',
    cost: 0,
    effect: { type: 'containSpread' },
    droppable: true,
    dropLevel: 181,
    maxLevel: 200,
    dropChance: 0.15,
    rarity: 'rare',
    world: 'ancient_ruins'
  }
];

export const PERMANENT_UPGRADES = [
  // HP/Survival Upgrades
  {
    id: 'hp_boost',
    name: 'HP Boost',
    description: '+20 Max HP permanently',
    emoji: '💪',
    cost: 15,
    effect: { type: 'maxHP', value: 20 }
  },
  {
    id: 'hp_reservoir',
    name: 'HP Reservoir',
    description: '+25 Max HP (stacks with HP Boost)',
    emoji: '🫀',
    cost: 40,
    effect: { type: 'maxHP', value: 25 }
  },
  {
    id: 'faster_regen',
    name: 'Faster Regeneration',
    description: '20 HP every 5 min instead of 10',
    emoji: '⏰',
    cost: 20,
    effect: { type: 'regenTime', value: 5 }
  },
  {
    id: 'quick_recovery',
    name: 'Quick Recovery',
    description: 'HP regenerates 50% faster',
    emoji: '💚',
    cost: 35,
    effect: { type: 'regenSpeed', value: 0.5 }
  },
  {
    id: 'thick_skin',
    name: 'Thick Skin',
    description: 'Lose only 8 HP on fail (instead of 10)',
    emoji: '🧱',
    cost: 25,
    effect: { type: 'damageReduction', value: 2 }
  },
  {
    id: 'second_wind',
    name: 'Second Wind',
    description: 'Once per day, auto-heal 30 HP below 20',
    emoji: '💨',
    cost: 50,
    effect: { type: 'autoHeal', value: 30, trigger: 20 }
  },
  
  // Combat/Ability Upgrades
  {
    id: 'sword_upgrade',
    name: 'Sword Upgrade',
    description: 'Sword clears +1 larger area',
    emoji: '⚔️',
    cost: 30,
    effect: { type: 'swordBonus', value: 1 }
  },
  {
    id: 'double_sword',
    name: 'Double Sword',
    description: 'Use sword ability twice per level',
    emoji: '⚔️⚔️',
    cost: 45,
    effect: { type: 'swordUses', value: 2 }
  },
  {
    id: 'lucky_start',
    name: 'Lucky Start',
    description: '15% chance to start with special fruit',
    emoji: '🍀',
    cost: 30,
    effect: { type: 'luckyStart', value: 0.15 }
  },
  {
    id: 'cascade_master',
    name: 'Cascade Master',
    description: '+10% score for chain reactions (4+)',
    emoji: '🌊',
    cost: 35,
    effect: { type: 'cascadeBonus', value: 0.1 }
  },
  {
    id: 'fruit_whisperer',
    name: 'Fruit Whisperer',
    description: 'Hints show optimal move',
    emoji: '🔮',
    cost: 40,
    effect: { type: 'betterHints', value: true }
  },
  {
    id: 'emergency_shield',
    name: 'Emergency Shield',
    description: 'Once per day, survive fail with 1 move',
    emoji: '🛡️',
    cost: 60,
    effect: { type: 'emergencyShield', value: 1 }
  },
  
  // Economy Upgrades
  {
    id: 'start_bonus',
    name: 'Start Bonus',
    description: '+2 moves at each level',
    emoji: '🎯',
    cost: 25,
    effect: { type: 'startMoves', value: 2 }
  },
  {
    id: 'fruit_magnet',
    name: 'Fruit Magnet',
    description: 'Target fruit appears 20% more often',
    emoji: '🧲',
    cost: 35,
    effect: { type: 'targetBoost', value: 0.2 }
  },
  {
    id: 'star_collector',
    name: 'Star Collector',
    description: '+1 bonus star on 3-star completions',
    emoji: '⭐',
    cost: 40,
    effect: { type: 'bonusStar', value: 1 }
  },
  {
    id: 'bargain_hunter',
    name: 'Bargain Hunter',
    description: '10% discount on all shop items',
    emoji: '🏷️',
    cost: 50,
    effect: { type: 'shopDiscount', value: 0.1 }
  },
  {
    id: 'daily_bonus_plus',
    name: 'Daily Bonus+',
    description: 'Double daily login rewards',
    emoji: '📅',
    cost: 30,
    effect: { type: 'dailyBonus', value: 2 }
  },
  {
    id: 'xp_enthusiasm',
    name: 'XP Enthusiasm',
    description: '+15% XP from all sources',
    emoji: '📈',
    cost: 40,
    effect: { type: 'xpBoost', value: 0.15 }
  },
  
  // Quality of Life
  {
    id: 'undo_move',
    name: 'Undo Move',
    description: 'One free undo per level',
    emoji: '↩️',
    cost: 75,
    effect: { type: 'undoMove', value: 1 }
  },
  {
    id: 'preview_mode',
    name: 'Preview Mode',
    description: 'See the next 3 fruits that will drop',
    emoji: '👁️',
    cost: 35,
    effect: { type: 'fruitPreview', value: 3 }
  },
  {
    id: 'speed_demon',
    name: 'Speed Demon',
    description: '2x animation speed option',
    emoji: '⚡',
    cost: 25,
    effect: { type: 'speedOption', value: 2 }
  }
];

// Get item by ID
export const getItemById = (itemId) => {
  return [...CONSUMABLES, ...PERMANENT_UPGRADES].find(item => item.id === itemId);
};

export function useShop() {
  const [totalStars, setTotalStars] = useState(0);
  const [totalCoins, setTotalCoins] = useState(0); // Coins earned from level completion
  const [earnedStarsLevels, setEarnedStarsLevels] = useState({}); // {levelId: starsEarned}
  const [inventory, setInventory] = useState({}); // {itemId: quantity} for consumables
  const [collectedFruits, setCollectedFruits] = useState({}); // {fruitId: quantity} for fruits
  const [permanentUpgrades, setPermanentUpgrades] = useState([]); // [itemId, itemId, ...]
  const [equipment, setEquipment] = useState([]); // Equipment items owned
  const [equippedItems, setEquippedItems] = useState({}); // Currently equipped {slot: itemId}
  const [battlePass, setBattlePass] = useState(null); // Battle Pass data
  const [isLoading, setIsLoading] = useState(true);

  // Function to refresh all shop data from backend
  const refreshShopData = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTotalStars(data.stars || 0);
        setTotalCoins(data.coins || 0);
        setEarnedStarsLevels(data.earned_stars_levels || {});
        setInventory(data.inventory || {});
        setCollectedFruits(data.collected_fruits || {});
        setPermanentUpgrades(data.permanent_upgrades || []);
        setEquipment(data.equipment || []);
        setEquippedItems(data.equipped_items || {});
        setBattlePass(data.battle_pass || null);
        return true;
      }
    } catch (error) {
      console.error('Failed to refresh shop data:', error);
    }
    return false;
  }, []);

  // Load shop data from backend/localStorage
  useEffect(() => {
    const loadShopData = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      let loadedFromBackend = false;
      
      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            setTotalStars(data.stars || 0);
            setTotalCoins(data.coins || 0);
            setEarnedStarsLevels(data.earned_stars_levels || {});
            setInventory(data.inventory || {});
            setCollectedFruits(data.collected_fruits || {});
            setPermanentUpgrades(data.permanent_upgrades || []);
            setEquipment(data.equipment || []);
            setEquippedItems(data.equipped_items || {});
            setBattlePass(data.battle_pass || null);
            loadedFromBackend = true;
            
            // Save upgrades to localStorage and trigger health recalculation
            if (data.permanent_upgrades && data.permanent_upgrades.length > 0) {
              localStorage.setItem('fruitCrush_permanentUpgrades', JSON.stringify(data.permanent_upgrades));
              window.dispatchEvent(new Event('storage'));
            }
          }
        } catch (error) {
          console.error('Failed to load shop data:', error);
        }
      }
      
      // Only use localStorage as fallback if backend didn't load
      if (!loadedFromBackend) {
        const localStars = localStorage.getItem('fruitCrush_stars');
        const localCoins = localStorage.getItem('fruitCrush_coins');
        const localEarned = localStorage.getItem('fruitCrush_earnedStarsLevels');
        const localInventory = localStorage.getItem('fruitCrush_inventory');
        const localUpgrades = localStorage.getItem('fruitCrush_permanentUpgrades');
        const localBattlePass = localStorage.getItem('fruitCrush_battlePass');
        
        if (localStars) setTotalStars(parseInt(localStars, 10));
        if (localCoins) setTotalCoins(parseInt(localCoins, 10));
        if (localEarned) setEarnedStarsLevels(JSON.parse(localEarned));
        if (localInventory) setInventory(JSON.parse(localInventory));
        if (localUpgrades) setPermanentUpgrades(JSON.parse(localUpgrades));
        if (localBattlePass) setBattlePass(JSON.parse(localBattlePass));
      }
      
      setIsLoading(false);
    };

    loadShopData();
  }, []);

  // Sync shop data to backend
  const syncToBackend = useCallback(async (stars, coins, earned, inv, upgrades) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/progress`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          stars,
          coins,
          earned_stars_levels: earned,
          inventory: inv,
          permanent_upgrades: upgrades
        })
      });
    } catch (error) {
      console.error('Failed to sync shop data:', error);
    }
  }, []);

  // Save to localStorage and sync
  const saveData = useCallback((stars, coins, earned, inv, upgrades) => {
    localStorage.setItem('fruitCrush_stars', stars.toString());
    localStorage.setItem('fruitCrush_coins', coins.toString());
    localStorage.setItem('fruitCrush_earnedStarsLevels', JSON.stringify(earned));
    localStorage.setItem('fruitCrush_inventory', JSON.stringify(inv));
    localStorage.setItem('fruitCrush_permanentUpgrades', JSON.stringify(upgrades));
    // Dispatch storage event to trigger health recalculation with new upgrades
    window.dispatchEvent(new Event('storage'));
    syncToBackend(stars, coins, earned, inv, upgrades);
  }, [syncToBackend]);

  // Award stars for completing a level (only first time)
  const awardStars = useCallback((levelId, stars) => {
    if (stars <= 0) return 0;
    
    const levelKey = levelId.toString();
    const previousStars = earnedStarsLevels[levelKey] || 0;
    
    // Only award if new stars are higher than previously earned
    if (stars > previousStars) {
      const newStarsEarned = stars - previousStars;
      const newTotal = totalStars + newStarsEarned;
      const newEarned = { ...earnedStarsLevels, [levelKey]: stars };
      
      setTotalStars(newTotal);
      setEarnedStarsLevels(newEarned);
      saveData(newTotal, totalCoins, newEarned, inventory, permanentUpgrades);
      
      return newStarsEarned;
    }
    
    return 0;
  }, [totalStars, totalCoins, earnedStarsLevels, inventory, permanentUpgrades, saveData]);

  // Calculate coins earned based on stars and score
  // Formula: Base coins by stars + score bonus
  // 1 star: 5 base + 1 per 1000 pts
  // 2 stars: 10 base + 2 per 1000 pts  
  // 3 stars: 20 base + 3 per 1000 pts
  const calculateCoins = useCallback((starsEarned, score) => {
    const baseCoins = starsEarned === 1 ? 5 : starsEarned === 2 ? 10 : starsEarned >= 3 ? 20 : 0;
    const scoreBonus = Math.floor(score / 1000) * starsEarned;
    return baseCoins + scoreBonus;
  }, []);

  // Award coins for completing a level
  const awardCoins = useCallback((coinsToAdd) => {
    if (coinsToAdd <= 0) return 0;
    
    const newTotal = totalCoins + coinsToAdd;
    setTotalCoins(newTotal);
    saveData(totalStars, newTotal, earnedStarsLevels, inventory, permanentUpgrades);
    
    return coinsToAdd;
  }, [totalStars, totalCoins, earnedStarsLevels, inventory, permanentUpgrades, saveData]);

  // Buy a consumable item
  const buyConsumable = useCallback((itemId) => {
    const item = CONSUMABLES.find(i => i.id === itemId);
    if (!item || totalStars < item.cost) return false;

    const newTotal = totalStars - item.cost;
    const newInventory = { ...inventory, [itemId]: (inventory[itemId] || 0) + 1 };
    
    setTotalStars(newTotal);
    setInventory(newInventory);
    saveData(newTotal, totalCoins, earnedStarsLevels, newInventory, permanentUpgrades);
    
    return true;
  }, [totalStars, totalCoins, inventory, earnedStarsLevels, permanentUpgrades, saveData]);

  // Buy a permanent upgrade
  const buyPermanentUpgrade = useCallback((itemId) => {
    const item = PERMANENT_UPGRADES.find(i => i.id === itemId);
    if (!item || totalStars < item.cost || permanentUpgrades.includes(itemId)) return false;

    const newTotal = totalStars - item.cost;
    const newUpgrades = [...permanentUpgrades, itemId];
    
    setTotalStars(newTotal);
    setPermanentUpgrades(newUpgrades);
    saveData(newTotal, totalCoins, earnedStarsLevels, inventory, newUpgrades);
    
    return true;
  }, [totalStars, totalCoins, permanentUpgrades, earnedStarsLevels, inventory, saveData]);

  // Use a consumable item (decrease quantity) - renamed to avoid React hooks rule
  const consumeItem = useCallback((itemId) => {
    if (!inventory[itemId] || inventory[itemId] <= 0) return false;

    const newInventory = { ...inventory, [itemId]: inventory[itemId] - 1 };
    setInventory(newInventory);
    saveData(totalStars, totalCoins, earnedStarsLevels, newInventory, permanentUpgrades);
    
    return true;
  }, [inventory, totalStars, totalCoins, earnedStarsLevels, permanentUpgrades, saveData]);

  // Check if player has a permanent upgrade
  const hasUpgrade = useCallback((itemId) => {
    return permanentUpgrades.includes(itemId);
  }, [permanentUpgrades]);

  // Get quantity of a consumable
  const getConsumableCount = useCallback((itemId) => {
    return inventory[itemId] || 0;
  }, [inventory]);

  // Get all active permanent upgrade effects
  const getActiveEffects = useCallback(() => {
    const effects = {};
    permanentUpgrades.forEach(upgradeId => {
      const upgrade = PERMANENT_UPGRADES.find(u => u.id === upgradeId);
      if (upgrade) {
        effects[upgrade.effect.type] = upgrade.effect.value;
      }
    });
    return effects;
  }, [permanentUpgrades]);

  // Add stars directly (for rewards like wheel of fortune)
  const addStars = useCallback((amount) => {
    if (amount <= 0) return;
    
    const newTotal = totalStars + amount;
    setTotalStars(newTotal);
    saveData(newTotal, totalCoins, earnedStarsLevels, inventory, permanentUpgrades);
  }, [totalStars, totalCoins, earnedStarsLevels, inventory, permanentUpgrades, saveData]);

  // Add item to inventory directly (for rewards)
  const addToInventory = useCallback((itemId, quantity = 1) => {
    if (quantity <= 0) return;
    
    const newInventory = { ...inventory, [itemId]: (inventory[itemId] || 0) + quantity };
    setInventory(newInventory);
    saveData(totalStars, totalCoins, earnedStarsLevels, newInventory, permanentUpgrades);
  }, [inventory, totalStars, totalCoins, earnedStarsLevels, permanentUpgrades, saveData]);

  // Add Battle Pass XP after level completion (10% of game score)
  const addBattlePassXP = useCallback(async (gameScore) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/progress/battle-pass-xp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ game_score: gameScore })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local battle pass state
        if (data.success) {
          setBattlePass(prev => ({
            ...prev,
            current_xp: data.new_total_xp,
            current_tier: data.new_tier
          }));
        }
        return data;
      }
    } catch (error) {
      console.error('Failed to add Battle Pass XP:', error);
    }
    return null;
  }, []);

  // Equip equipment item
  const equipEquipment = useCallback(async (itemId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/equipment/equip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_id: itemId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setEquippedItems(data.equipped_items || {});
        return true;
      }
    } catch (error) {
      console.error('Failed to equip equipment:', error);
    }
    return false;
  }, []);

  // Unequip equipment item
  const unequipEquipment = useCallback(async (itemId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/equipment/unequip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_id: itemId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setEquippedItems(data.equipped_items || {});
        return true;
      }
    } catch (error) {
      console.error('Failed to unequip equipment:', error);
    }
    return false;
  }, []);

  // Get equipment status (uses, recharge, etc.)
  const getEquipmentStatus = useCallback(async (itemId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/equipment/status/${itemId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to get equipment status:', error);
    }
    return null;
  }, []);

  // Use equipment (decrement uses)
  const consumeEquipmentUse = useCallback(async (itemId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/equipment/use/${itemId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to use equipment:', error);
    }
    return null;
  }, []);

  // Recharge equipment
  const rechargeEquipment = useCallback(async (itemId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/equipment/recharge/${itemId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        return await response.json();
      }
      const error = await response.json();
      return { error: true, message: error.detail };
    } catch (error) {
      console.error('Failed to recharge equipment:', error);
    }
    return null;
  }, []);

  return {
    totalStars,
    totalCoins,
    inventory,
    collectedFruits,
    equipment,
    equippedItems,
    permanentUpgrades,
    battlePass,
    isLoading,
    awardStars,
    awardCoins,
    calculateCoins,
    buyConsumable,
    buyPermanentUpgrade,
    consumeItem,
    hasUpgrade,
    getConsumableCount,
    getActiveEffects,
    earnedStarsLevels,
    addStars,
    addToInventory,
    addBattlePassXP,
    refreshShopData,
    // Equipment functions
    equipEquipment,
    unequipEquipment,
    getEquipmentStatus,
    consumeEquipmentUse,
    rechargeEquipment
  };
}
